import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import {
  ListTodo, ClipboardCheck, Users, AlertTriangle,
  ArrowUpRight, Hammer, TrendingUp, ArrowRight, Sparkles,
  UserCog, CalendarDays, CalendarClock, Clock, CheckCircle2, BarChart3
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, AreaChart, Area
} from "recharts";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";
import ProfileEditDialog from "@/components/ProfileEditDialog";
import MiniCalendar from "@/components/MiniCalendar";
import WorkRequestsPanel from "@/components/WorkRequestsPanel";
import { toast } from "sonner";

const NAVY = "#1a1a1a";
const SCARLET = "#ff4500";
const COLORS = [NAVY, SCARLET, "#1a8754", "#d6a900", "#8b00ff"];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [taskAnalytics, setTaskAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [runs, setRuns] = useState([]);
  const [home, setHome] = useState(null);
  const [myAudits, setMyAudits] = useState({ today: [], this_week: [] });
  const [editOpen, setEditOpen] = useState(false);

  const loadHome = () => api.get("/users/me/home").then((r) => setHome(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview").then((r) => setOverview(r.data)),
      api.get("/tasks/analytics").then((r) => setTaskAnalytics(r.data)),
      api.get("/trends/overview?days=30").then((r) => setTrends(r.data)),
      api.get("/checklist-runs").then((r) => setRuns(r.data)),
      api.get("/audit-assignments/mine").then((r) => setMyAudits(r.data)).catch(() => {}),
    ]).catch(() => {});
    loadHome();
  }, []);

  const statusData = taskAnalytics
    ? Object.entries(taskAnalytics.by_status).map(([k, v]) => ({ name: k, value: v }))
    : [];
  const tagData = taskAnalytics
    ? Object.entries(taskAnalytics.by_tag).map(([k, v]) => ({ name: k, count: v }))
    : [];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = (user?.name || "Manager").split(" ")[0];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Hero greeting strip */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Sparkles className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Live · {formatDate(new Date())}</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              {greeting},<br/><span className="text-[#ff5f15]" data-testid="dash-greeting-name">{firstName}.</span>
            </h1>
            {(user?.title || user?.role) && (
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55" data-testid="dash-user-title">
                {user?.title || user?.role} {user?.org?.name ? `· ${user.org.name}` : ""}
              </div>
            )}
            <p className="mt-2 text-sm text-white/75 max-w-md">
              You have <b className="text-white">{home?.counters?.to_do ?? overview?.tasks_open ?? "—"}</b> open task{(home?.counters?.to_do ?? 0) === 1 ? "" : "s"} and <b className="text-white">{overview?.risk?.buckets?.critical ?? 0}</b> critical-risk action{(overview?.risk?.buckets?.critical ?? 0) === 1 ? "" : "s"} on the board.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="dash-edit-profile" onClick={() => setEditOpen(true)} className="btn-pill btn-pill-ghost border border-white/25">
              <UserCog className="w-3.5 h-3.5" /> Edit profile
            </button>
            <button data-testid="dash-my-analytics" onClick={() => nav("/app/me/analytics")} className="btn-pill btn-pill-ghost border border-white/25">
              <BarChart3 className="w-3.5 h-3.5" /> My analytics
            </button>
            <Link to="/app/checklists" data-testid="dash-cta-audit" className="btn-pill btn-pill-primary">
              Run an audit <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* MY DAY — assigned-to-me counters + due-today checklist */}
      <MyDayPanel home={home} onChanged={loadHome} />

      {/* iter30 — Audits nudge widget (only renders if user has any audit assignments) */}
      <AuditsNudge myAudits={myAudits} />

      {/* iter34 — Calendar embedded on Command Center (replaces standalone /app/calendar page) */}
      <MiniCalendar />

      {/* Personal pulse — migrated from the old /app/me/analytics page */}
      <PersonalPulseCard userId={user?.id} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={ListTodo} label="Open tasks" value={overview?.tasks_open ?? "—"} accent />
        <Kpi icon={ListTodo} label="Total tasks" value={overview?.tasks_total ?? "—"} />
        <Kpi icon={ClipboardCheck} label="Audit runs" value={overview?.runs_completed ?? "—"} />
        <Kpi icon={Users} label="Hubs" value={overview?.teams ?? "—"} />
      </div>

      {/* PDCA + 8-Wastes + Risk Register lean strip */}
      <PdcaWastesPanel overview={overview} />
      <RiskRegisterStrip overview={overview} />

      {/* Trend card + charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <TrendScoreCard trends={trends} />
        <div className="lg:col-span-2 surface-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Compliance trend (30 days)</div>
              <div className="font-display font-bold text-base">Fail rate over time</div>
            </div>
            <Link to="/app/reports" data-testid="dash-trend-link" className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] inline-flex items-center gap-1 hover:text-[#ff4500]">
              Full report <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <AreaChart data={trends?.series || []}>
                <defs>
                  <linearGradient id="failGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SCARLET} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={SCARLET} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,139,0.08)" />
                <XAxis dataKey="date" stroke={NAVY} fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis stroke={NAVY} fontSize={10} unit="%" />
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
                <Area type="monotone" dataKey="fail_rate_pct" stroke={SCARLET} strokeWidth={2} fill="url(#failGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tags bar + status pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 surface-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Tags volume</div>
              <div className="font-display font-bold text-base">Tasks by tag</div>
            </div>
            <Link to="/app/tasks" data-testid="dash-view-tasks" className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] inline-flex items-center gap-1 hover:text-[#ff4500]">
              View ledger <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <BarChart data={tagData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,139,0.08)" />
                <XAxis dataKey="name" stroke={NAVY} fontSize={10} />
                <YAxis stroke={NAVY} fontSize={10} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
                <Bar dataKey="count" fill={NAVY} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="surface-card rounded-2xl p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Status</div>
          <div className="font-display font-bold text-base">By status</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-mono">
            {statusData.map((s, i) => (
              <span key={s.name} className="inline-flex items-center gap-1 text-[#1a1a1a]/80">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: COLORS[i % COLORS.length] }} />
                {s.name} · {s.value}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Recent runs / work requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Recent audit runs" testId="dash-runs-panel" cta={<Link to="/app/checklists" data-testid="dash-view-checklists" className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] hover:text-[#ff4500]">Open →</Link>}>
          {runs.length === 0 && <Empty msg="No audit runs yet" />}
          <ul className="divide-y divide-[#1a1a1a]/08">
            {runs.slice(0, 5).map((r) => {
              const fails = r.items?.filter((i) => i.status === "fail").length || 0;
              return (
                <li key={r.id} className="py-3 flex items-center justify-between">
                  <div className="min-w-0 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#fff1ea] flex items-center justify-center">
                      <ClipboardCheck className="w-4 h-4 text-[#1a1a1a]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">{r.template_name}</div>
                      <div className="text-[10px] font-mono text-[#1a1a1a]/55 uppercase tracking-wider truncate">
                        {r.location || "no location"} · {r.status}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {fails > 0 && (
                      <span className="inline-flex items-center gap-1 bg-[#ff4500] text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold">
                        <AlertTriangle className="w-3 h-3" /> {fails}
                      </span>
                    )}
                    <Link to={`/app/checklists/run/${r.id}`} data-testid={`open-run-${r.id}`} className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] hover:text-[#ff4500]">Open</Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </Panel>
        <WorkRequestsPanel />
      </div>

      <ProfileEditDialog open={editOpen} onClose={() => setEditOpen(false)} user={user}
        onSaved={() => refreshUser?.()} />
    </div>
  );
}

function MyDayPanel({ home }) {
  const c = home?.counters || {};
  return (
    <section className="space-y-4" data-testid="my-day-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <DayKpi testId="myday-due-today" icon={CalendarDays} label="Due today" value={c.due_today ?? "—"} accent />
        <DayKpi testId="myday-due-week" icon={CalendarClock} label="Due this week" value={c.due_this_week ?? "—"} />
        <DayKpi testId="myday-due-later" icon={Clock} label="Due later" value={c.due_later ?? "—"} />
        <DayKpi testId="myday-to-do" icon={ListTodo} label="To-do total" value={c.to_do ?? "—"} />
      </div>
      {/* iter52 — overdue items are now folded into the calendar's "Today" cell (with X-days-overdue markers). No separate panel needed. */}
    </section>
  );
}

function DayKpi({ icon: Icon, label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`surface-card rounded-2xl p-4 ${accent ? "ring-2 ring-[#ff4500]/30" : ""}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-[#ff4500]" : "text-[#1a1a1a]"}`}>{value}</div>
    </div>
  );
}

function TrendScoreCard({ trends }) {
  const score = trends?.trend_score ?? 0;
  const fr = trends?.fail_rate_pct ?? 0;
  const runs = trends?.total_runs ?? 0;
  const tone = score >= 80 ? "#1a8754" : score >= 55 ? "#d6a900" : "#ff4500";
  return (
    <div className="surface-card rounded-2xl p-6 relative overflow-hidden" data-testid="trend-score-card">
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-15" style={{ background: tone }} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Compliance trend</div>
            <div className="font-display font-bold text-base">Trend score</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#fff1ea] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[#1a1a1a]" />
          </div>
        </div>
        <div className="mt-5 flex items-end gap-3">
          <div className="font-display font-black text-6xl leading-none" style={{ color: tone }}>{score}</div>
          <div className="text-sm text-[#1a1a1a]/65 mb-2">/ 100</div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-[#fff1ea] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, score)}%`, background: `linear-gradient(90deg, ${tone}, ${tone}cc)` }} />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="bg-[#f7f5f2] rounded-xl px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/55">Fail rate</div>
            <div className="font-display font-bold text-lg text-[#1a1a1a]">{fr}%</div>
          </div>
          <div className="bg-[#f7f5f2] rounded-xl px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/55">Audits (30d)</div>
            <div className="font-display font-bold text-lg text-[#1a1a1a]">{runs}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="surface-card rounded-2xl p-4 relative overflow-hidden" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      {accent && <div className="absolute top-0 left-0 bottom-0 w-1 bg-[#ff4500]" />}
      <div className="flex items-center gap-2 text-[#1a1a1a]/65">
        <Icon className="w-3.5 h-3.5" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className="font-display font-black text-3xl mt-2 text-[#1a1a1a] leading-none">{value}</div>
    </div>
  );
}

function RiskRegisterStrip({ overview }) {
  const risk = overview?.risk || { buckets: { low: 0, medium: 0, high: 0, critical: 0 }, weighted: 0 };
  const dueEff = overview?.effectiveness_due || 0;
  const b = risk.buckets || {};
  const total = (b.low || 0) + (b.medium || 0) + (b.high || 0) + (b.critical || 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dashboard-risk">
      <div className="surface-card rounded-2xl p-5 lg:col-span-2 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#ff4500]/15" />
        <div className="relative flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Risk Register</div>
            <div className="font-display font-bold text-base">Likelihood × Severity profile</div>
          </div>
          <span className="iso-chip iso-chip-orange">Weighted · {risk.weighted}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["low", "Low", b.low || 0, "Score 1–4", "#1a8754"],
            ["medium", "Medium", b.medium || 0, "Score 5–9", "#d6a900"],
            ["high", "High", b.high || 0, "Score 10–15 · 5-Whys required", "#ff5f15"],
            ["critical", "Critical", b.critical || 0, "Score 16–25 · 5-Whys required", "#ff4500"],
          ].map(([k, l, v, sub, color]) => {
            const pct = total ? Math.round((v / total) * 100) : 0;
            return (
              <div key={k} className="bg-[#f7f5f2] rounded-xl p-3" data-testid={`risk-${k}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] font-bold rounded-full px-2 py-0.5 text-white" style={{ background: color }}>{l}</div>
                  <div className="font-display font-black text-3xl" style={{ color }}>{v}</div>
                </div>
                <div className="text-[10px] text-[#1a1a1a]/65 mt-2">{sub}</div>
                <div className="mt-2 h-1 bg-white rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="surface-card rounded-2xl p-5 relative overflow-hidden" data-testid="effectiveness-due-card">
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#ff4500]/10" />
        <div className="relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// 30-day verification</div>
          <div className="font-display font-bold text-base">Effectiveness reviews due</div>
          <div className="mt-4 flex items-end gap-3">
            <div className="font-display font-black text-6xl text-[#ff4500] leading-none">{dueEff}</div>
            <div className="text-sm text-[#1a1a1a]/65 mb-2">action{dueEff === 1 ? "" : "s"} ready for 30-day verification</div>
          </div>
          <Link to="/app/actions" className="mt-4 inline-flex btn-pill btn-pill-secondary">
            Review now →
          </Link>
        </div>
      </div>
    </div>
  );
}

function PdcaWastesPanel({ overview }) {
  const pdca = overview?.pdca || { plan: 0, do: 0, check: 0, act: 0 };
  const waste = overview?.waste_counts || {};
  const totalWaste = Object.values(waste).reduce((a, b) => a + b, 0) || 0;
  const wasteList = [
    ["transport", "Transport"], ["inventory", "Inventory"], ["motion", "Motion"],
    ["waiting", "Waiting"], ["overproduction", "Overprod."], ["overprocessing", "Overproc."],
    ["defects", "Defects"], ["skills", "Skills"],
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 surface-card rounded-2xl p-5" data-testid="dashboard-pdca">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// PDCA cycle</div>
            <div className="font-display font-bold text-base">Plan · Do · Check · Act</div>
          </div>
          <span className="iso-chip iso-chip-orange">Continuous improvement</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[["P", "Plan", pdca.plan, "Open actions"],
            ["D", "Do", pdca.do, "In progress"],
            ["C", "Check", pdca.check, "Audits this wk"],
            ["A", "Act", pdca.act, "Completed 30d"]].map(([k, l, v, sub]) => (
            <div key={k} className="bg-[#f7f5f2] rounded-xl p-3 text-center">
              <div className="w-9 h-9 rounded-full bg-[#1a1a1a] text-white font-display font-black flex items-center justify-center mx-auto">{k}</div>
              <div className="font-display font-black text-2xl mt-2 text-[#ff4500]">{v}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#1a1a1a]/55 mt-0.5">{l}</div>
              <div className="text-[10px] text-[#1a1a1a]/55 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3 surface-card rounded-2xl p-5" data-testid="dashboard-wastes">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// 8 Wastes</div>
            <div className="font-display font-bold text-base">Where waste is hiding</div>
          </div>
          <span className="iso-chip iso-chip-carbon">{totalWaste} tagged</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {wasteList.map(([k, l]) => {
            const v = waste[k] || 0;
            const pct = totalWaste ? Math.round((v / totalWaste) * 100) : 0;
            return (
              <div key={k} className={`rounded-xl p-3 ${v > 0 ? "bg-[#fff1ea] border border-[#ff4500]/20" : "bg-[#f7f5f2] border border-transparent"}`}>
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]">{k[0].toUpperCase()}</div>
                <div className="font-display font-black text-xl mt-1" style={{ color: v > 0 ? "#ff4500" : "#1a1a1a" }}>{v}</div>
                <div className="text-[10px] text-[#1a1a1a]/65">{l}</div>
                <div className="mt-1.5 h-1 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-[#ff4500]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, cta, children, testId }) {
  return (
    <div className="surface-card rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Live</div>
          <div className="font-display font-bold text-base">{title}</div>
        </div>
        {cta}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="py-10 text-center bg-[#f7f5f2] rounded-xl border border-dashed border-[#1a1a1a]/15">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{msg}</div>
    </div>
  );
}


/** Personal pulse — quick-glance activity roll-up for the signed-in user.
 *  Migrated from the old standalone /app/me/analytics page.
 */
function PersonalPulseCard({ userId }) {
  const [preset, setPreset] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!userId) return;
    const today = new Date();
    const daysMap = { today: 0, "7d": 6, "30d": 29, "90d": 89 };
    const days = daysMap[preset] ?? 6;
    const to = today.toISOString().slice(0, 10);
    const frm = new Date(today.getTime() - days * 86400000).toISOString().slice(0, 10);
    setLoading(true);
    api.get(`/users/${userId}/analytics`, { params: { frm, to } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, preset]);
  const c = data?.counters || {};
  const presets = [
    { k: "today", label: "Today" },
    { k: "7d", label: "7 days" },
    { k: "30d", label: "30 days" },
    { k: "90d", label: "90 days" },
  ];
  return (
    <div className="surface-card rounded-2xl p-5" data-testid="personal-pulse-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Your operational pulse</div>
          <div className="font-display font-bold text-base">What you&apos;ve done in the window</div>
        </div>
        <div className="flex bg-[#f7f5f2] rounded-full p-1">
          {presets.map((p) => (
            <button key={p.k} data-testid={`pulse-preset-${p.k}`} onClick={() => setPreset(p.k)}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${preset === p.k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/55"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <PulseMini icon={ListTodo} label="Tasks open" value={c.tasks_open ?? "—"} />
        <PulseMini icon={CheckCircle2} label="Tasks done" value={c.tasks_completed ?? "—"} tone="#1a8754" />
        <PulseMini icon={AlertTriangle} label="Actions open" value={c.actions_open ?? "—"} tone="#ff4500" />
        <PulseMini icon={CheckCircle2} label="Actions closed" value={c.actions_completed ?? "—"} tone="#1a8754" />
        <PulseMini icon={ClipboardCheck} label="Audits run" value={c.checklist_runs_created ?? "—"} />
        <PulseMini icon={Users} label="Comments" value={c.comments_posted ?? "—"} />
      </div>
      <div className="mt-3 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
        {loading ? "Refreshing…" : `Window ${data?.from || "—"} → ${data?.to || "—"}`}
        <Link to="/app/me/analytics" data-testid="personal-pulse-deepdive" className="ml-auto text-[#ff4500] hover:underline">Deep dive →</Link>
      </div>
    </div>
  );
}

function PulseMini({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-[#f7f5f2] rounded-xl p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-display font-black text-2xl mt-1" style={{ color: tone || "#1a1a1a" }}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter30 — Audits Nudge widget
// One-glance "you owe X audits by end-of-shift" panel. Only renders if
// GET /api/audit-assignments/mine returns any items in today OR this_week.
// ---------------------------------------------------------------------------
function AuditsNudge({ myAudits }) {
  const today = myAudits?.today || [];
  const week = myAudits?.this_week || [];
  if (today.length === 0 && week.length === 0) return null;
  return (
    <section data-testid="audits-nudge" className="surface-card rounded-2xl overflow-hidden">
      <div className={`px-5 py-3 flex items-start gap-3 ${today.length > 0 ? "bg-[#fff1ea] border-b border-[#ff4500]/20" : "bg-[#faf8f4] border-b border-[#1a1a1a]/06"}`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${today.length > 0 ? "bg-[#ff4500] text-white animate-pulse" : "bg-[#d6a900] text-white"}`}>
          <span className="font-display font-black text-lg">{today.length || week.length}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: today.length > 0 ? "#ff4500" : "#a37e00" }}>
            // Assigned audits
          </div>
          <div className="font-display font-black text-base text-[#1a1a1a]">
            {today.length > 0
              ? `You owe ${today.length} audit${today.length === 1 ? "" : "s"} by end-of-shift.`
              : `${week.length} audit${week.length === 1 ? "" : "s"} coming up this week.`}
          </div>
          <div className="text-[11px] text-[#1a1a1a]/65">Scheduled by your admin — click any to start the run.</div>
        </div>
      </div>
      <div className="divide-y divide-[#1a1a1a]/06">
        {today.map((a) => <NudgeRow key={a.id} a={a} due="today" />)}
        {week.map((a) => <NudgeRow key={a.id} a={a} due="this week" />)}
      </div>
    </section>
  );
}

function NudgeRow({ a, due }) {
  const dueColor = due === "today" ? "#ff4500" : "#a37e00";
  return (
    <Link to="/app/checklists" data-testid={`audit-nudge-${a.id}`}
      className="flex items-center gap-3 px-5 py-2.5 hover:bg-[#faf8f4] transition-colors">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dueColor }} />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm text-[#1a1a1a] truncate">{a.template_name}</div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          {a.display_id} · {a.schedule_type}{a.schedule_time ? ` @ ${a.schedule_time}` : ""}
        </div>
      </div>
      <span className="text-[9px] font-mono uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ background: dueColor }}>
        due {due}
      </span>
    </Link>
  );
}
